/**
 * Monatsstatus eines Deal-Monats aus seinen Rechnungen (WS-04) und abgeleiteter Deal-Status
 * (WS-09: keine Handschalter für „Marke hat bezahlt“). Dazu Buchung, Schutz, Aktivität und
 * Umsatzsicht (KA-03 … KA-21, KZ-10/11/16).
 */
import { awaitsDelivery, isCancelled, isLive, isPaid, isSent, needsCorrection } from "./invoice-facts.js";
import { addMonths } from "./periods.js";
import type { Cents, InvoiceRecord, IsoDate, MonthKey } from "./types.js";

// ---------------------------------------------------------------------------
// Konfiguration: Status-Katalog und Modelle der Agentur
// ---------------------------------------------------------------------------

export type TerminalKind = "completed" | "cancelled" | "archived" | "blocked";

export interface DealStatusDef {
  key: string;
  /** zählt als (voraussichtlicher) Umsatz – z. B. ab „Vertrag unterschrieben“ (KZ-10) */
  countsAsRevenue: boolean;
  terminal?: TerminalKind;
}

export interface DealModelDef {
  key: string;
  /** Event/Gifting o. ä. zählen nie als Umsatz */
  revenue: boolean;
  /** Monats-Lebenszyklus (KA-03) */
  monthBased: boolean;
}

export interface DealCatalog {
  statuses: DealStatusDef[];
  models: DealModelDef[];
}

export function statusDef(catalog: DealCatalog, key: string | null | undefined): DealStatusDef | undefined {
  return catalog.statuses.find((s) => s.key === key);
}

// ---------------------------------------------------------------------------
// Monatsstatus (WS-04, KA-10, KA-19)
// ---------------------------------------------------------------------------

/** Aus Rechnungen abgeleitete Monatsstände, vom am wenigsten zum am weitesten fortgeschrittenen. */
export const MONTH_PROGRESS = ["invoice_submitted", "invoice_approved", "invoice_at_customer", "paid"] as const;
export type DerivedMonthStatus = (typeof MONTH_PROGRESS)[number] | "action_required" | "delivery_failed";

/**
 * Rechnungsmail an die Marke zurückgekommen bzw. nicht zugestellt und seitdem nicht zugegangen (SICHT-AGENTUR-GELD-21):
 * Der Monat ist nicht „bei der Marke“, die Agentur muss neu zustellen.
 */
export function deliveryFailed(inv: InvoiceRecord): boolean {
  return !isPaid(inv) && awaitsDelivery(inv) && inv.deliveryFailedAt != null;
}

export interface MonthStatusResult {
  status: string;
  source: "invoices" | "manual" | "requested" | "default";
}

/**
 * Rechnungen, die den Monatsstatus bestimmen: wirksam, nicht storniert, kein reiner Entwurf. Eine Stornorechnung hebt nur ihre
 * Rechnung auf und ist selbst kein Stand des Monats (Praxistest K-138: „Rechnung freigegeben“ nach dem Storno).
 */
export function activeMonthInvoices(invoices: readonly InvoiceRecord[]): InvoiceRecord[] {
  return invoices.filter((i) => isLive(i) && !isCancelled(i) && !i.creditFor && i.reviewStatus !== "not_submitted");
}

function progressOf(inv: InvoiceRecord): number {
  if (isPaid(inv)) return 3;
  if (inv.reviewStatus === "approved") return isSent(inv) ? 2 : 1;
  return 0;
}

/**
 * WS-04: Braucht eine Rechnung des Monats eine Korrektur → „Handlung nötig“. Ist eine Rechnungsmail an die Marke
 * gescheitert und nicht zugegangen → „Versand gescheitert“ (GELD-21). Sonst der am
 * wenigsten fortgeschrittene Stand der aktiven Rechnungen. Ohne aktive Rechnungen: von der
 * Agentur gesetzter Monatsstatus → angefordert → „offen“ (KA-10).
 */
export function deriveMonthStatus(input: {
  invoices: readonly InvoiceRecord[];
  manualStatus?: string | null;
  invoiceRequestedAt?: IsoDate | null;
}): MonthStatusResult {
  const active = activeMonthInvoices(input.invoices);
  if (active.some(needsCorrection)) return { status: "action_required", source: "invoices" };
  if (active.some(deliveryFailed)) return { status: "delivery_failed", source: "invoices" };
  if (active.length > 0) {
    const min = Math.min(...active.map(progressOf));
    return { status: MONTH_PROGRESS[min]!, source: "invoices" };
  }
  if (input.manualStatus) return { status: input.manualStatus, source: "manual" };
  if (input.invoiceRequestedAt) return { status: "invoice_requested", source: "requested" };
  return { status: "open", source: "default" };
}

// ---------------------------------------------------------------------------
// Buchung und Schutz (KA-05, KA-06)
// ---------------------------------------------------------------------------

export interface MonthBooking {
  customerPaidAt: IsoDate | null;
  talentPaidAt: IsoDate | null;
  booked: boolean;
}

/**
 * KA-05: gebucht, wenn Marken-Zahlung UND Talent-Meldung vorliegen oder von Hand gebucht wurde.
 * Beide Stempel werden aus den Rechnungen abgeleitet (wirksam, nicht storniert, nicht zurückgegeben).
 */
export function deriveMonthBooking(input: { invoices: readonly InvoiceRecord[]; manualBookedAt?: IsoDate | null }): MonthBooking {
  const relevant = input.invoices.filter((i) => isLive(i) && !isCancelled(i) && !needsCorrection(i));
  const min = (xs: (IsoDate | null)[]) => xs.filter((x): x is IsoDate => !!x).sort()[0] ?? null;
  // Bezahlt ohne eigenes Datum (RE-04: Status genügt) bucht mit dem frühesten belegten Zahlungsschritt (R-13)
  const customerPaidAt = min(relevant.filter(isPaid).map((i) => i.paidAt ?? i.talentPaymentResponseAt ?? i.adminPaymentConfirmedAt ?? null));
  const talentPaidAt = min(relevant.filter((i) => i.talentPaymentResponse === "paid").map((i) => i.talentPaymentResponseAt));
  return { customerPaidAt, talentPaidAt, booked: !!input.manualBookedAt || (!!customerPaidAt && !!talentPaidAt) };
}

/**
 * KA-06: Monate mit echten Daten sind geschützt und werden beim Speichern nie gelöscht.
 * Konnten die Rechnungen nicht gelesen werden, ist jeder Monat geschützt.
 */
export function isMonthProtected(input: {
  invoices: readonly InvoiceRecord[];
  invoicesReadFailed?: boolean;
  manualBookedAt?: IsoDate | null;
  customerPaidAt?: IsoDate | null;
  talentPaidAt?: IsoDate | null;
  invoiceRequestedAt?: IsoDate | null;
}): boolean {
  if (input.invoicesReadFailed) return true;
  if (input.manualBookedAt || input.customerPaidAt || input.talentPaidAt || input.invoiceRequestedAt) return true;
  return input.invoices.some((i) => isLive(i));
}

// ---------------------------------------------------------------------------
// Monats-Lebenszyklus, Erledigt, Aktivität (KA-03, KA-11, KA-12)
// ---------------------------------------------------------------------------

/** KA-03: Monatsbasiert, wenn Zeitmodus monatlich/Raten ODER das Modell monatsbasiert ist. */
export function isMonthBased(catalog: DealCatalog, modelKey: string, timeMode: "once" | "monthly" | "installments"): boolean {
  if (timeMode !== "once") return true;
  return !!catalog.models.find((m) => m.key === modelKey)?.monthBased;
}

/** Offene Rechnung eines Monats: wirksam, nicht storniert, nicht bezahlt, kein Entwurf. */
export function hasOpenInvoice(invoices: readonly InvoiceRecord[]): boolean {
  return invoices.some((i) => isLive(i) && !isCancelled(i) && !isPaid(i) && i.reviewStatus !== "not_submitted");
}

/** KA-11: Monat erledigt, wenn Status bezahlt/abgeschlossen/storniert UND keine offene Rechnung. */
export function isMonthDone(catalog: DealCatalog, month: { status: string; invoices: readonly InvoiceRecord[] }): boolean {
  const t = statusDef(catalog, month.status)?.terminal;
  const doneStatus = month.status === "paid" || t === "completed" || t === "cancelled";
  return doneStatus && !hasOpenInvoice(month.invoices);
}

export interface DealMonth {
  month: MonthKey;
  status: string;
  invoices: readonly InvoiceRecord[];
  invoiceRequestedAt?: IsoDate | null;
  booked?: boolean;
  amountCents?: Cents | null;
}

/**
 * KA-12: Ein monatlicher Deal bleibt trotz Endstatus aktiv – Abgeschlossen solange ein Monat
 * nicht erledigt ist, Storniert nur solange ein Monat in der Abrechnungskette steckt.
 * Archivierte nie. Ohne Endstatus immer aktiv.
 */
export function isDealActive(catalog: DealCatalog, dealStatus: string, months: readonly DealMonth[]): { active: boolean; openMonths: number } {
  const t = statusDef(catalog, dealStatus)?.terminal;
  if (!t) return { active: true, openMonths: months.filter((m) => !isMonthDone(catalog, m)).length };
  if (t === "archived") return { active: false, openMonths: 0 };
  if (t === "completed") {
    const open = months.filter((m) => !isMonthDone(catalog, m)).length;
    return { active: open > 0, openMonths: open };
  }
  const inChain = months.filter((m) => hasOpenInvoice(m.invoices) || (!!m.invoiceRequestedAt && !isMonthDone(catalog, m))).length;
  return { active: inChain > 0, openMonths: inChain };
}

// ---------------------------------------------------------------------------
// Deal-Status aus Rechnungen (WS-09, KA-14, KA-15, RE-44-T)
// ---------------------------------------------------------------------------

export interface DealStatusResult {
  /** Anzeigestatus */
  status: string;
  /** „Marke hat bezahlt“ – nur abgeleitet, nie von Hand */
  brandPaid: boolean;
  source: "terminal" | "invoices" | "manual";
}

/**
 * Deal-Status: ein Endstatus aus dem Katalog (storniert, archiviert, blockiert, abgeschlossen) wird nie
 * überschrieben. Sonst folgt der Status dem abgeleiteten Monatsstatus (Einmal-Deal: der eine
 * Monat; Monats-Deal: der am wenigsten fortgeschrittene aktive Monat). Ohne Rechnungen gilt
 * der Pipeline-Status der Agentur. „Marke hat bezahlt“ ist wahr, wenn alle aktiven Rechnungen
 * bezahlt sind.
 */
export function deriveDealStatus(catalog: DealCatalog, input: { pipelineStatus: string; months: readonly DealMonth[] }): DealStatusResult {
  const all = input.months.flatMap((m) => activeMonthInvoices(m.invoices));
  const brandPaid = all.length > 0 && all.every(isPaid);
  const t = statusDef(catalog, input.pipelineStatus)?.terminal;
  // KA-14: Endstatus wird nie überschrieben, auch „abgeschlossen“ (R-14). Ob ein Deal noch aktiv ist, regelt KA-12 (isDealActive).
  if (t === "cancelled" || t === "archived" || t === "blocked" || t === "completed") return { status: input.pipelineStatus, brandPaid, source: "terminal" };

  const derived = input.months
    .map((m) => deriveMonthStatus({ invoices: m.invoices, invoiceRequestedAt: m.invoiceRequestedAt ?? null }))
    .filter((r) => r.source === "invoices" || r.source === "requested");
  if (derived.length === 0) return { status: input.pipelineStatus, brandPaid, source: "manual" };
  if (derived.some((d) => d.status === "action_required")) return { status: "action_required", brandPaid, source: "invoices" };
  if (derived.some((d) => d.status === "delivery_failed")) return { status: "delivery_failed", brandPaid, source: "invoices" };
  const order = ["invoice_requested", ...MONTH_PROGRESS] as string[];
  const min = Math.min(...derived.map((d) => order.indexOf(d.status)));
  return { status: order[min]!, brandPaid, source: "invoices" };
}

// ---------------------------------------------------------------------------
// Umsatz (KA-13, KA-21, KZ-10, KZ-11, KZ-16)
// ---------------------------------------------------------------------------

export interface DealRevenue {
  bookedCents: Cents;
  plannedCents: Cents;
  countedMonths: MonthKey[];
}

/**
 * Umsatzsicht eines Deals.
 * - Modelle ohne Umsatz (Event, Gifting) zählen nie; Status vor „zählt als Umsatz“ zählen nicht.
 * - Gebucht nur mit Buchung (Zahlungsnachweis); „abgeschlossen“ allein ist Plan (KZ-11).
 * - Storniert/Blockiert wirken nur auf die Zukunft: gebuchte Monate bleiben, ungebuchte fallen weg (KA-13).
 * - Monate ohne Betrag (z. B. reine Provision) zählen nicht (KA-21).
 */
export function dealRevenue(catalog: DealCatalog, input: { modelKey: string; status: string; months: readonly DealMonth[] }): DealRevenue {
  const model = catalog.models.find((m) => m.key === input.modelKey);
  const st = statusDef(catalog, input.status);
  const empty: DealRevenue = { bookedCents: 0, plannedCents: 0, countedMonths: [] };
  if (!model?.revenue) return empty;
  const futureCut = st?.terminal === "cancelled" || st?.terminal === "blocked";
  if (!st?.countsAsRevenue && !futureCut) return empty;
  let booked = 0;
  let planned = 0;
  const counted: MonthKey[] = [];
  for (const m of input.months) {
    const amount = m.amountCents ?? 0;
    if (amount <= 0) continue;
    if (m.booked) {
      booked += amount;
      counted.push(m.month);
    } else if (!futureCut) {
      planned += amount;
      counted.push(m.month);
    }
  }
  return { bookedCents: booked, plannedCents: planned, countedMonths: counted };
}

/**
 * KZ-16: Ein Plan-Monat direkt neben einem Ist-Monat (±1), das selbst keinen Plan hat, wird
 * nicht zusätzlich gezählt (bekannter Monatsversatz). Rückgabe: Summe und gezählte Monate.
 */
export function mergePlanWithActuals(
  plan: ReadonlyMap<MonthKey, Cents>,
  actual: ReadonlyMap<MonthKey, Cents>,
): { totalCents: Cents; byMonth: Map<MonthKey, { cents: Cents; source: "actual" | "plan" }> } {
  const byMonth = new Map<MonthKey, { cents: Cents; source: "actual" | "plan" }>();
  for (const [m, c] of actual) byMonth.set(m, { cents: c, source: "actual" });
  const consumed = new Set<MonthKey>();
  for (const a of [...actual.keys()].sort()) {
    if (plan.has(a)) continue;
    for (const p of [addMonths(a, -1), addMonths(a, 1)]) {
      if (plan.has(p) && !actual.has(p) && !consumed.has(p)) {
        consumed.add(p);
        break;
      }
    }
  }
  for (const [m, c] of plan) {
    if (actual.has(m) || consumed.has(m)) continue;
    byMonth.set(m, { cents: c, source: "plan" });
  }
  let total = 0;
  for (const v of byMonth.values()) total += v.cents;
  return { totalCents: total, byMonth };
}
